import React, {useEffect, useMemo, useRef} from 'react'
import {Chart, LogarithmicScale} from 'chart.js/auto'
import {chartColors} from '../../mosaic/charts/ChartjsConfigOrig.jsx'
import {chartAreaGradient} from '../../mosaic/charts/ChartjsConfigOrig'
import {hexToRGB, tailwindConfig} from '../../mosaic/utils/Utils'
import {useThemeProvider} from '../../mosaic/utils/ThemeContext.jsx'
import {GraphScale, GraphType} from './Graph.types.ts'
import {useRenderCurrencyValue} from "../../api/utilities/useValueRenderers.js";
import {DateTimeFormatter, LocalDateTime} from "@js-joda/core";
import moment from 'moment';

export default function LineChart({ data, className, style }) {
    const canvas = useRef(null)
    const { currentTheme } = useThemeProvider()
    const darkMode = useMemo(() => currentTheme === 'dark', [currentTheme])
    const {
        gridColor,
        textColor,
        tooltipBodyColor,
        tooltipBgColor,
        tooltipBorderColor,
        tooltipTitleColor,
    } = chartColors

    let renderCurrencyValue = useRenderCurrencyValue();

    useEffect(() => {
        const ctx = canvas.current
        if (!ctx) return

        if (data.scale === GraphScale.Logarithmic) {
            Chart.register(LogarithmicScale)
        }

        const chart = new Chart(ctx, {
            type: data.type,
            data: {
                datasets: data.datasets.map((ds, index) => {
                    // Pie nebo vlastní backgroundColor
                    if (ds.backgroundColor && data.type === GraphType.Pie) {
                        return {
                            label: ds.label,
                            data: ds.data,
                            backgroundColor: ds.backgroundColor,
                            borderColor: ds.backgroundColor,
                            borderWidth: 1,
                        }
                    }
                    // line styling + gradient
                    const raw = ds.gray
                        ? tailwindConfig().theme.colors.gray[500]
                        : tailwindConfig().theme.colors.violet[500]
                    const semitrans = `rgba(${hexToRGB(raw)},0.25)`

                    const out = {
                        label: ds.label,
                        data: ds.data,
                        parsing: ds.parsing,
                        fill: true,
                        borderWidth: 2,
                        tension: 0.2,
                        clip: 20,
                        pointRadius: 0,
                        pointHoverRadius: 3,
                        pointBackgroundColor: ds.gray ? semitrans : raw,
                        pointHoverBackgroundColor: ds.gray ? semitrans : raw,
                    }

                    if (!ds.gray) {
                        out.backgroundColor = (ctx) => {
                            const { ctx: c, chartArea } = ctx.chart
                            return chartAreaGradient(c, chartArea, [
                                { stop: 0, color: `rgba(${hexToRGB(raw)},0)` },
                                { stop: 1, color: `rgba(${hexToRGB(raw)},0.2)` },
                            ])
                        }
                        out.borderColor = raw
                    } else {
                        out.backgroundColor = semitrans
                        out.borderColor = semitrans
                    }

                    return out
                }),
            },
            options: {
                animations: null,
                responsive: true,
                maintainAspectRatio: false,
                layout: { padding: 0 },
                scales: {
                    y: {
                        display: false,
                        type: data.scale,
                        border: { display: false },
                        ticks: {
                            display: false,
                            maxTicksLimit: 7,
                            color: darkMode ? textColor.dark : textColor.light,
                        },
                        grid: {
                            display: false
                        },
                    },
                    x: {
                        display: false,
                        border: { display: false },
                        ticks: {
                            display: false,
                            padding: 0,
                            align: 'center',
                            color: darkMode ? textColor.dark : textColor.light,
                            callback: (_v, idx) => {
                                const orig = Object.keys(data.datasets[0].data)[idx]
                                return data.tickRender ? data.tickRender(orig) : orig
                            },
                        },
                        grid: { display: false },
                    },
                },
                interaction: {
                    mode: 'nearest',
                    intersect: false,
                },
                plugins: {
                    tooltip: {
                        enabled: true,
                        titleColor: darkMode ? tooltipTitleColor.dark : tooltipTitleColor.light,
                        callbacks: {
                            title: (ctx) => {
                                // 1) vezmeme první tooltipItem
                                const ti = Array.isArray(ctx) ? ctx[0] : ctx;
                                const idx = ti?.dataIndex;
                                const data = ti?.dataset?.data;
                                const raw = data?.[idx]?.title;

                                if (!raw) {
                                    return '';
                                }

                                // 2) pokud už je to moment objekt, jen ho naformátujeme
                                if (moment.isMoment(raw)) {
                                    return raw.format('D.M.YYYY');
                                }

                                // 3) jinak to nejprve zkusíme načíst jako YYYY-MM-DD v _strict_ modu
                                const m = moment(String(raw), 'YYYY-MM-DD', true);
                                if (m.isValid()) {
                                    return m.format('D.M.YYYY');
                                }

                                // 4) jako poslední možnost můžete zkusit js-joda
                                try {
                                    const d = LocalDate.parse(raw);
                                    return d.format(DateTimeFormatter.ofPattern('d.M.yyyy'));
                                } catch {
                                    return String(raw); // fallback
                                }
                            },

                            label: (ctx) => {
                                const idx  = ctx?.dataIndex;
                                const item = ctx?.dataset?.data?.[idx];

                                // If there's no item or it's not a number, render nothing
                                if (!item || typeof item.value !== 'number') {
                                    return '';
                                }

                                // currency formatting
                                if (item.type === 'money' && item.currency) {
                                    return renderCurrencyValue({
                                        amount: item.value * 100,
                                        currency: item.currency,
                                    });
                                }

                                // percentage formatting
                                if (item.type === 'percentage') {
                                    // round to 2 decimal places and append %
                                    const pct = item.value.toFixed(2);
                                    return `${pct}%`;
                                }

                                // Otherwise just show the raw value
                                return String(item.value);
                            },
                        },
                        bodyColor: darkMode
                            ? tooltipBodyColor.dark
                            : tooltipBodyColor.light,
                        backgroundColor: darkMode
                            ? tooltipBgColor.dark
                            : tooltipBgColor.light,
                        borderColor: darkMode
                            ? tooltipBorderColor.dark
                            : tooltipBorderColor.light,
                        opacity: 0.8,
                    },
                    legend: { display: false },
                },
                elements: {
                    point: {
                        radius: 0,
                        hoverRadius: 3,
                    },
                },
            },
        })

        return () => {
            chart.destroy()
            if (data.scale === GraphScale.Logarithmic) {
                Chart.unregister(LogarithmicScale)
            }
        }
    }, [data, darkMode])

    return <canvas ref={canvas} className={className} style={style} />
}
